import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelopeOpenText } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPPORT_NAV, PRIORITY_COLORS } from './nav';

interface Item {
    id: string; type: string; priority: string; direction: string; title: string; body: string;
    institution: string | null; clinic: string | null; recipient: string | null; is_read: boolean; created_at: string;
}

const OmnichannelInbox: React.FC = () => {
    const { api } = useInternalApi();
    const [items, setItems] = useState<Item[]>([]);
    const [loading, setLoading] = useState(true);
    const [priority, setPriority] = useState('');
    const [unreadOnly, setUnreadOnly] = useState(false);

    useEffect(() => { document.title = 'Omnichannel Inbox - Uzima Internal'; }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (priority) params.set('priority', priority);
            if (unreadOnly) params.set('unread', 'true');
            const res = await api.get(`admin-dashboard/support/inbox/?${params}`);
            if (res.data?.status === 'success') setItems(res.data.data || []);
        } catch { setItems([]); } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priority, unreadOnly]);

    useEffect(() => { load(); }, [load]);

    return (
        <InternalShell activeDashboard="support_center" subNav={SUPPORT_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faEnvelopeOpenText} className="me-2 text-primary" />Omnichannel Inbox</h2>
            <p className="text-muted mb-4">Communication items across every institution on the platform</p>

            <div className="d-flex gap-2 mb-3 align-items-center">
                <select className="form-select" style={{ maxWidth: 200 }} value={priority} onChange={(e) => setPriority(e.target.value)}>
                    <option value="">All priorities</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                </select>
                <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="unreadOnly" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
                    <label className="form-check-label" htmlFor="unreadOnly">Unread only</label>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-5 text-muted">No inbox items match.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Title</th><th>Institution</th><th>Recipient</th><th>Priority</th><th>Read</th><th>When</th></tr></thead>
                            <tbody>
                                {items.map(i => (
                                    <tr key={i.id} className={!i.is_read ? 'fw-semibold' : ''}>
                                        <td>{i.title}<div className="small text-muted fw-normal">{i.body}</div></td>
                                        <td>{i.institution || '—'}<div className="small text-muted fw-normal">{i.clinic || ''}</div></td>
                                        <td>{i.recipient || '—'}</td>
                                        <td><span className={`badge bg-${PRIORITY_COLORS[i.priority] || 'secondary'}`}>{i.priority}</span></td>
                                        <td>{i.is_read ? <span className="text-muted">Read</span> : <span className="badge bg-primary">New</span>}</td>
                                        <td className="small">{new Date(i.created_at).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </InternalShell>
    );
};

export default OmnichannelInbox;
